import { GenericModal } from "components/modals/GenericModal";
import { ModalProps } from "components/types/ModalProps";
import { FC, FormEvent, useCallback, useState } from "react";
import { accountLiterals } from "../literals";
import NftImage from "components/images/NftImage";
import { INft } from "logic/types/nft/INft";
import {
  useAddressSelectionLogic,
  useErrorAlert,
  useNftTitle,
} from "logic/hooks";
import { AddressInput } from "../AddressInput";
import CtaSecondary from "components/CtaSecondary";
import CtaPrimary from "components/CtaPrimary";
import { WalletType } from "contracts/types";
import InputErrorMessage from "components/modals/shared/InputErrorMessage";
import { Address } from "@unlockdfinance/verislabs-web3";
import classNames from "classnames";
import { handleErrors } from "errors";
import LoadingSpinner from "components/LoadingSpinner";
import unlockdWalletModule from "logic/UnlockdWalletModule";
import useAlertNotification from "components/hooks/useAlertNotification";
import { lifeCycleLiterals } from "literals";

const { cancelCta, subtitle, title, transferCta, transferDetail } =
  accountLiterals.modalWithdrawNfts;

const { onSignPending, onSuccess, onTxPending } =
  lifeCycleLiterals.withdrawNfts;

type Props = ModalProps & {
  nft: INft;
  onNftWithdrawn?: (nft: INft) => void;
};

export const ModalWithdrawNft: FC<Props> = (props) => {
  const { isOpen, toggleModal, nft, onNftWithdrawn } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const {
    addressInputValue,
    addressError,
    handleAddressChange: handleAddressToSendChange,
    validateInputAddress,
  } = useAddressSelectionLogic();
  const [error, setError] = useState<Error | null>(null);
  useErrorAlert(error);
  const [, openAlertNotification] = useAlertNotification();

  const nftTitle = useNftTitle(nft);

  const handleTransferNfts = async (to: Address) => {
    await nft.transfer(
      unlockdWalletModule.unlockdAddress!,
      to,
      {
        onSignaturePending: () => {
          setLoading(true);
          openAlertNotification("info", onSignPending, 5000000);
        },
        onLoading: () => {
          openAlertNotification("info", onTxPending, 5000000);
        },
      },
      WalletType.SMART
    );

    openAlertNotification("success", onSuccess, 5000);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateInputAddress()) {
      try {
        setLoading(true);

        await handleTransferNfts(addressInputValue as Address);

        onNftWithdrawn?.(nft);

        toggleModal(false);
      } catch (err) {
        setError(handleErrors(err, "withdrawNfts"));
        setLoading(false);
      }
    }
  };

  return (
    <GenericModal
      className="!pr-[60px] !pl-[60px] !pt-[30px] !pb-[30px] flex flex-col items-center justify-center"
      headerTitle={title}
      headerSubtitle={subtitle}
      isOpen={isOpen}
      toggleModal={toggleModal}
      onSubmit={handleSubmit}
    >
      <NftImage
        className="mt-[15px] w-[140px] h-[140px]"
        src={nft.image}
        alt={nft.alt}
      />
      <span className="mt-[15px] text-center">
        {transferDetail(nftTitle || "")}
      </span>
      {isLoading ? (
        <div className="w-full h-[110px] flex justify-center items-center">
          <LoadingSpinner className="w-10 h-10" />
        </div>
      ) : (
        <>
          <AddressInput
            className={classNames(
              "w-[526px] h-[87px] mt-[5px]",
              addressError ? "!border-myred" : ""
            )}
            defaultValue={addressInputValue}
            onChange={handleAddressToSendChange}
          />
          <div className="w-[526px] mt-2 h-2.5">
            {addressError && <InputErrorMessage error={addressError} />}
          </div>
        </>
      )}

      <div className="mt-3 flex justify-center gap-[22px]">
        <CtaSecondary
          className="w-[164px]"
          type="button"
          onClick={() => toggleModal()}
        >
          {cancelCta}
        </CtaSecondary>
        <CtaPrimary className="w-[164px]" type="submit" disabled={isLoading}>
          {transferCta}
        </CtaPrimary>
      </div>
    </GenericModal>
  );
};
